import { type ReactNode } from 'react';
import { CopyButton } from '../shared/CopyButton';
import { cn } from '../../lib/utils';
import { HighlightedCode, useDocumentTheme, useHighlightedCommands } from './highlighted-command';

interface CommandBlockProps {
  /** One or more shell commands. Pass a string for a single command, or an array for multiple. */
  commands: string | string[];
  /** Optional label shown in the header bar (e.g. "2 commands"). */
  title?: ReactNode;
  /** Show a `$` prompt before each command. Defaults to `true`. */
  prompt?: boolean;
  /** Compact density for inline contexts. */
  compact?: boolean;
  /** Fit width to content instead of stretching full-width. */
  inline?: boolean;
  /**
   * Stable hook for product tours / tests, applied as `data-tutorial-id` on the
   * copy button. Only meaningful for a single command (one copy button).
   */
  copyTutorialId?: string;
  className?: string;
}

/**
 * Terminal-styled block for displaying read-only shell commands. Shows each
 * command in monospace with an optional `$` prompt and per-command copy
 * button. When multiple commands are provided, a header with a count and
 * copy-all button is rendered. Commands are syntax-highlighted as bash.
 *
 * Visual language matches `CodeBlock` (same border/surface treatment) but
 * is purpose-built for commands: prompt gutter, line-level copy, and
 * word-boundary wrapping instead of `break-all`.
 */
export function CommandBlock({
  commands: commandsProp,
  title,
  prompt = true,
  compact = false,
  inline = false,
  copyTutorialId,
  className,
}: CommandBlockProps) {
  const commands = Array.isArray(commandsProp) ? commandsProp : [commandsProp];
  const theme = useDocumentTheme();
  const tokensByCommand = useHighlightedCommands(commands, theme);

  if (commands.length === 0) return null;

  const joined = commands.join('\n');
  const showHeader = title != null || commands.length > 1;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border border-border/40 bg-muted/30',
        inline && 'w-fit max-w-full',
        className,
      )}
    >
      {showHeader && (
        <div className="flex items-center justify-between border-b border-border/30 px-3 py-1.5">
          <span className="text-2xs font-medium text-muted-foreground">
            {title ?? `${commands.length} commands`}
          </span>
          <CopyButton
            text={joined}
            className="h-5 w-5 shrink-0 text-muted-foreground transition-colors hover:text-foreground"
          />
        </div>
      )}
      <div className={cn(commands.length > 1 && 'divide-y divide-border/20')}>
        {commands.map((cmd, i) => (
          <div
            key={i}
            className={cn(
              // `items-center` keeps the prompt and copy button aligned with a
              // single-line command (the common case). The prompt is pinned to
              // the first line via `self-start` so it stays put when a long
              // command wraps to multiple lines.
              'group/cmd flex items-center gap-2',
              compact ? 'px-2.5 py-1.5' : 'px-3 py-2',
            )}
          >
            {prompt && (
              <span
                aria-hidden
                className={cn(
                  'shrink-0 select-none self-start font-mono text-muted-foreground/40',
                  compact ? 'text-2xs leading-relaxed' : 'text-xs leading-relaxed',
                )}
              >
                $
              </span>
            )}
            <pre
              className={cn(
                'min-w-0 flex-1 font-mono leading-relaxed whitespace-pre-wrap break-words',
                compact ? 'text-2xs' : 'text-xs',
                !tokensByCommand[i] && 'text-foreground/90',
              )}
            >
              {tokensByCommand[i] ? <HighlightedCode tokens={tokensByCommand[i]} /> : cmd}
            </pre>
            {!showHeader && (
              <CopyButton
                text={cmd}
                dataTutorialId={copyTutorialId}
                className="h-4 w-4 shrink-0 text-muted-foreground transition-colors hover:text-foreground"
              />
            )}
            {showHeader && (
              <CopyButton
                text={cmd}
                className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover/cmd:opacity-100 hover:text-foreground"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
